<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="InsuranceDiscoveryList" label="Blanket VOB Search" />
      <breadcrumb-link view="InsuranceDiscoveryAsInquiry" label="Create Inquiry" :active="true" />
    </breadcrumb-list>
    <section>
      <div class="container is-max-hd">
        <div class="columns">
          <div class="column is-two-thirds">
            <div class="box" style="position: relative">
              <h1 class="title">Create Inquiry from Blanket VOB Search</h1>
              <hr />
              <form @submit.prevent="handleSubmit">
                <div class="block">
                  <AjaxSelectField
                    label="Select a provider"
                    placeholder="e.g. Healthcare Facility"
                    :options="providers"
                    :errors="errors.provider_id"
                    v-model="form.provider_id"
                    :loading="loadingProviders"
                  />
                  <AjaxSelectField
                    label="Select a payer"
                    placeholder="e.g. Insurance Company"
                    :options="payers"
                    :errors="errors.payer_id"
                    v-model="form.payer_id"
                    :loading="loadingPayers"
                    @input="handlePayerSelected"
                  />
                </div>

                <hr />

                <div v-if="schema">
                  <div class="columns" v-if="showCombos">
                    <div class="column">
                      <b-field label="Select Fields">
                        <b-select expanded v-model="form.selectedCombo">
                          <option v-for="(option, index) in combos" :value="index" :key="index">
                            {{ option.join(', ') }}
                          </option>
                        </b-select>
                      </b-field>
                    </div>
                  </div>

                  <div class="columns">
                    <a
                      v-if="!showCombos && combos.length > 1"
                      @click="showCombos = !showCombos"
                      style="padding: 0.75rem"
                    >
                      <small>Have different information?</small>
                    </a>
                  </div>

                  <div class="columns">
                    <div
                      class="column"
                      v-if="combos[form.selectedCombo]?.includes('memberId') || errors.member_id"
                    >
                      <b-field
                        label="Member ID"
                        :message="errors.member_id"
                        :type="{ 'is-danger': errors.member_id }"
                      >
                        <b-input v-model="form.member_id" placeholder="Enter member ID" required />
                      </b-field>
                    </div>
                    <div
                      class="column"
                      v-if="
                        combos[form.selectedCombo]?.includes('patientBirthDate') ||
                        errors.patient_dob
                      "
                    >
                      <b-field
                        label="Patient Date of Birth"
                        :message="errors.patient_dob"
                        :type="{ 'is-danger': errors.patient_dob }"
                      >
                        <b-datepicker
                          v-model="form.patient_dob"
                          :min-date="minDate"
                          :max-date="maxDate"
                          :date-parser="dateParser"
                          editable
                          locale="en-US"
                          placeholder="Click to select..."
                          icon="calendar-today"
                          :mobile-modal="false"
                          :mobile-native="false"
                          trap-focus
                          :use-html5-validation="false"
                          required
                        />
                      </b-field>
                    </div>
                  </div>

                  <div class="columns">
                    <div
                      class="column"
                      v-if="
                        combos[form.selectedCombo]?.includes('patientFirstName') ||
                        errors.patient_first_name
                      "
                    >
                      <b-field
                        label="Patient First Name"
                        :message="errors.patient_first_name"
                        :type="{ 'is-danger': errors.patient_first_name }"
                      >
                        <b-input
                          v-model="form.patient_first_name"
                          placeholder="Enter first name"
                          required
                        />
                      </b-field>
                    </div>
                    <div
                      class="column"
                      v-if="
                        combos[form.selectedCombo]?.includes('patientLastName') ||
                        errors.patient_last_name
                      "
                    >
                      <b-field
                        label="Patient Last Name"
                        :message="errors.patient_last_name"
                        :type="{ 'is-danger': errors.patient_last_name }"
                      >
                        <b-input
                          v-model="form.patient_last_name"
                          placeholder="Enter last name"
                          required
                        />
                      </b-field>
                    </div>
                  </div>

                  <div class="columns">
                    <div class="column">
                      <div class="block" v-if="schema?.serviceType?.allowed">
                        <MultiCheckboxField
                          v-if="schema.serviceType.multiple"
                          v-model="form.service_types"
                          :schema="schema.serviceType"
                          :errors="errors.service_types"
                        />
                        <RadioField
                          v-if="!schema.serviceType.multiple"
                          v-model="form.service_types"
                          :schema="schema.serviceType"
                          :errors="errors.service_types"
                        />
                      </div>
                    </div>
                    <div class="column">
                      <label class="label">Patient's Relationship to Subscriber</label>
                      <div class="control">
                        <div class="radio-group">
                          <b-radio
                            v-model="form.subscriber_relationship"
                            name="subscriber_relationship"
                            native-value="18"
                            required
                          >
                            Self
                          </b-radio>
                          <b-radio
                            v-model="form.subscriber_relationship"
                            name="subscriber_relationship"
                            native-value="01"
                            required
                          >
                            Spouse
                          </b-radio>
                          <b-radio
                            v-model="form.subscriber_relationship"
                            name="subscriber_relationship"
                            native-value="19"
                            required
                          >
                            Child
                          </b-radio>
                          <b-radio
                            v-model="form.subscriber_relationship"
                            name="subscriber_relationship"
                            native-value="20"
                            required
                          >
                            Employee
                          </b-radio>
                        </div>
                      </div>
                      <p v-if="errors.subscriber_relationship" class="help is-danger">
                        {{ errors.subscriber_relationship }}
                      </p>
                    </div>
                  </div>
                </div>

                <hr />

                <div class="columns is-vcentered">
                  <div class="column">
                    <div
                      class="field"
                      style="
                        margin-bottom: 0;
                        display: flex;
                        align-items: center;
                        margin-top: 0.75rem;
                      "
                    >
                      <b-switch
                        size="is-medium"
                        v-model="form.as_of_today"
                        :type="errors.as_of_date ? 'is-danger' : ''"
                        @input="handleAsOfTodayChange"
                      >
                        As Of Today
                      </b-switch>
                    </div>
                  </div>
                  <div class="column is-narrow">
                    <div class="buttons">
                      <b-button
                        type="is-light"
                        :class="{ 'is-loading': isSubmitting }"
                        :disabled="isSubmitting"
                        @click="handleSaveAsDraft"
                        size="is-medium"
                        style="background-color: #d3d2d2"
                      >
                        Save As Draft
                      </b-button>
                      <b-button
                        type="is-primary"
                        :class="{ 'is-loading': isSubmitting }"
                        :disabled="isSubmitting"
                        native-type="submit"
                        size="is-medium"
                      >
                        Create Inquiry
                      </b-button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <div class="column">
            <div class="box">
              <h2 class="title is-5">Instructions</h2>
              <div class="content">
                <p>
                  Please fill in all the required information to create a new inquiry from insurance
                  discovery:
                </p>
                <ul>
                  <li>Select a provider from the dropdown list</li>
                  <li>Select a payer from the dropdown list</li>
                  <li>Enter the member ID</li>
                  <li>Enter the patient's first and last name</li>
                  <li>Select the patient's date of birth</li>
                  <li>Select the subscriber relationship</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import request from '../request';
import AjaxSelectField from '../components/fields/AjaxSelectField.vue';
import MultiCheckboxField from '../components/fields/MultiCheckboxField.vue';
import RadioField from '../components/fields/RadioField.vue';

export default {
  name: 'InsuranceDiscoveryAsInquiry',
  components: {
    AjaxSelectField,
    MultiCheckboxField,
    RadioField,
  },
  data() {
    return {
      form: {
        provider_id: null,
        payer_id: null,
        member_id: '',
        patient_first_name: '',
        patient_last_name: '',
        patient_dob: null,
        subscriber_relationship: '18',
        service_types: [],
        selectedCombo: 0,
      },
      providers: [],
      payers: [],
      loadingProviders: true,
      loadingPayers: true,
      isSubmitting: false,
      errors: {},
      insuranceDiscovery: null,
      selectedPayer: null,
      schema: null,
      isDataLoaded: false,
      combos: [],
      showCombos: false,
    };
  },
  computed: {
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      return date;
    },
    maxDate() {
      return new Date();
    },
    serviceTypes() {
      if (!this.insuranceDiscovery || !this.selectedPayer) return [];
      const serviceDetails = this.getServiceDetails(this.selectedPayer);
      return serviceDetails ? serviceDetails.map((service) => service.ServiceName) : [];
    },
  },
  async created() {
    try {
      // Load providers and payers first
      await Promise.all([this.fetchProviders(), this.fetchPayers()]);
      this.isDataLoaded = true;

      // Then load insurance discovery
      await this.fetchInsuranceDiscovery();
    } catch (error) {
      this.$buefy.toast.open({
        message: 'Error loading initial data',
        type: 'is-danger',
      });
    }
  },
  methods: {
    dateParser(s) {
      if (s.length === 8) {
        s = `${s.substr(0, 2)}/${s.substr(2, 2)}/${s.substr(4, 4)}`;
      }
      const [month, day, year] = s.split('/').map((num) => parseInt(num, 10));
      const parsedDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
      const today = new Date();
      if (parsedDate > today || parsedDate < this.minDate) {
        return null;
      }
      return parsedDate;
    },
    async fetchProviders() {
      try {
        const response = await request('/providers');
        this.providers = response.data.providers;
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load providers',
          type: 'is-danger',
        });
      } finally {
        this.loadingProviders = false;
      }
    },
    async fetchPayers() {
      try {
        const response = await request('/payers');
        this.payers = response.data.payers;
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load payers',
          type: 'is-danger',
        });
      } finally {
        this.loadingPayers = false;
      }
    },
    async fetchInsuranceDiscovery() {
      if (!this.isDataLoaded) {
        return;
      }

      const insuranceDiscoveryId = this.$route.params.id;
      const payerResponseId = this.$route.query.payerResponseId;

      if (!insuranceDiscoveryId) {
        this.$buefy.toast.open({
          message: 'Insurance discovery ID is required',
          type: 'is-danger',
        });
        this.$router.push({ name: 'InsuranceDiscoveryList' });
        return;
      }

      try {
        const response = await request(`/blanket-vob-search/${insuranceDiscoveryId}`);
        this.insuranceDiscovery = response.data.insurance_discovery;

        // Pre-populate provider
        if (this.insuranceDiscovery.provider) {
          const providerExists = this.providers.some(
            (p) => p.id === this.insuranceDiscovery.provider.id
          );
          if (providerExists) {
            this.form.provider_id = this.insuranceDiscovery.provider.id;
          } else {
            this.errors.provider_id =
              'The provider used is no longer available, please select a different one.';
          }
        }

        // Populate patient information from insurance discovery
        if (this.insuranceDiscovery.patient_first_name) {
          this.form.patient_first_name = this.insuranceDiscovery.patient_first_name;
        }
        if (this.insuranceDiscovery.patient_last_name) {
          this.form.patient_last_name = this.insuranceDiscovery.patient_last_name;
        }
        if (this.insuranceDiscovery.dob) {
          // Parse the date string (MM/DD/YYYY) into a Date object
          const [month, day, year] = this.insuranceDiscovery.dob.split('/').map(Number);
          this.form.patient_dob = new Date(year, month - 1, day);
        }

        // Check if we have insurance_discovery_payers in the response
        if (
          this.insuranceDiscovery.insurance_discovery_payers &&
          this.insuranceDiscovery.insurance_discovery_payers.length > 0
        ) {
          if (payerResponseId) {
            this.selectedPayer = this.insuranceDiscovery.insurance_discovery_payers.find(
              (p) => p.id === payerResponseId
            );
          } else {
            this.selectedPayer = this.insuranceDiscovery.insurance_discovery_payers[0];
          }

          // Set member_id from selectedPayer if available, otherwise fallback
          if (this.selectedPayer && this.selectedPayer.member_id) {
            this.form.member_id = this.selectedPayer.member_id;
          } else if (this.insuranceDiscovery.member_id) {
            this.form.member_id = this.insuranceDiscovery.member_id;
          }

          // Pre-populate form with payer data
          if (this.selectedPayer) {
            // First check if the payer_code matches any top-level payer's pverify_code
            const directPayerMatch = this.payers.find(
              (p) => p.pverify_code === this.selectedPayer.payer_code
            );

            if (directPayerMatch) {
              this.form.payer_id = directPayerMatch.id;
            } else {
              // If no direct match, search through children
              let parentFound = false;
              for (const parentPayer of this.payers) {
                if (parentPayer.children && Array.isArray(parentPayer.children)) {
                  const childMatch = parentPayer.children.find(
                    (child) => child.pverify_code === this.selectedPayer.payer_code
                  );
                  if (childMatch) {
                    // Always select the parent payer
                    this.form.payer_id = parentPayer.id;
                    parentFound = true;
                    break;
                  }
                }
              }

              // If no parent found for the child, show error
              if (!parentFound) {
                this.form.payer_id = null;
                this.errors.payer_id =
                  'The payer used is no longer available, please select a different one.';
              }
            }

            // After setting the payer_id, trigger the schema load
            if (this.form.payer_id) {
              await this.handlePayerSelected(this.form.payer_id);
            }

            const serviceDetails = this.getServiceDetails(this.selectedPayer);
            if (serviceDetails) {
              // Map service names to their corresponding IDs from the schema
              this.form.service_types = serviceDetails
                .map((service) => {
                  const option = this.schema?.serviceType?.options?.find(
                    (opt) => opt.name === service.ServiceName
                  );
                  return option ? option.id : null;
                })
                .filter((id) => id !== null);
            }
          }
        }
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Error loading insurance discovery data',
          type: 'is-danger',
        });
      }
    },
    getServiceDetails(payer) {
      if (!payer?.payer_plan_response) return null;
      try {
        const response = JSON.parse(payer.payer_plan_response);
        return response?.ServiceDetails || [];
      } catch (e) {
        return null;
      }
    },
    async handleSubmit() {
      this.isSubmitting = true;
      this.errors = {};

      // Validate required fields
      if (!this.form.service_types || this.form.service_types.length === 0) {
        this.errors.service_types = 'Please select at least one service type';
        this.isSubmitting = false;
        return;
      }

      try {
        const response = await request('/inquiries', {
          method: 'POST',
          data: {
            provider_id: this.form.provider_id,
            payer_id: this.form.payer_id,
            memberId: this.form.member_id,
            patientBirthDate: this.form.patient_dob ? this.form.patient_dob.toISOString() : null,
            patientFirstName: this.form.patient_first_name,
            patientLastName: this.form.patient_last_name,
            subscriberRelationship: this.form.subscriber_relationship,
            serviceType: this.form.service_types,
            insurance_discovery_id: this.$route.params.id,
          },
        });

        const inquiry = response.data.inquiry;

        if (!inquiry) {
          this.$buefy.toast.open({
            message: 'Failed to create inquiry: No inquiry data received',
            type: 'is-danger',
          });
          this.isSubmitting = false;
          return;
        }

        if (inquiry.status === 'complete') {
          this.$buefy.toast.open({
            message: 'Inquiry created successfully',
            type: 'is-success',
          });

          // Wait for the inquiry to be complete before navigating
          const checkInquiryStatus = async () => {
            try {
              const statusResponse = await request(`/inquiries/${inquiry.id}`);
              const statusInquiry = statusResponse.data.inquiry;

              if (statusInquiry.status === 'complete' || statusInquiry.status === 'expired') {
                await this.$router.push({
                  name: 'InquiryDetail',
                  params: { id: inquiry.id },
                });
              } else if (statusInquiry.status === 'max-retry') {
                this.$buefy.toast.open({
                  message: 'The payer is taking a long time to respond. Please try again later.',
                  type: 'is-warning',
                  duration: 20000,
                });
                this.$router.push({ name: 'InquiryList' });
              } else if (statusInquiry.status === 'failure') {
                this.$buefy.toast.open({
                  message: 'The inquiry failed. Please check the details and try again.',
                  type: 'is-danger',
                  duration: 20000,
                });
                this.$router.push({ name: 'InquiryList' });
              } else {
                // If not complete, check again after a delay
                setTimeout(checkInquiryStatus, 1000);
              }
            } catch (error) {
              this.$buefy.toast.open({
                message: 'Error checking inquiry status',
                type: 'is-danger',
              });
            }
          };

          // Start checking the inquiry status
          checkInquiryStatus();
        } else if (inquiry.status === 'draft') {
          this.$router.push({ name: 'InquiryList' });
        } else if (inquiry.status === 'failure') {
          // Handle failure status
          let errorMessage = 'The inquiry failed.';

          // Check for specific error messages in remote_errors
          if (inquiry.remote_errors && Object.keys(inquiry.remote_errors).length > 0) {
            const errorMessages = Object.values(inquiry.remote_errors);
            errorMessage = errorMessages.join('. ');
          }

          this.$buefy.toast.open({
            message: errorMessage,
            type: 'is-danger',
            duration: 20000,
          });

          // Map remote errors to form fields if they exist
          if (inquiry.remote_errors) {
            for (let key in inquiry.remote_errors) {
              const errorMessage = inquiry.remote_errors[key];
              this.$set(this.errors, 'NOFIELD', errorMessage);

              // Map specific field errors
              if (key === 'APIResponseMessage' || key === 'ErrorDescription') {
                if (
                  errorMessage.toLowerCase().includes('dob') ||
                  errorMessage.toLowerCase().includes('date of birth') ||
                  errorMessage.toLowerCase().includes('birth date')
                ) {
                  this.$set(this.errors, 'patient_dob', errorMessage);
                }
                if (errorMessage.toLowerCase().includes('name')) {
                  if (errorMessage.toLowerCase().includes('first')) {
                    this.$set(this.errors, 'patient_first_name', errorMessage);
                  } else if (errorMessage.toLowerCase().includes('last')) {
                    this.$set(this.errors, 'patient_last_name', errorMessage);
                  }
                }
                if (
                  errorMessage.toLowerCase().includes('member') ||
                  errorMessage.toLowerCase().includes('id')
                ) {
                  this.$set(this.errors, 'member_id', errorMessage);
                }
              }
            }
          }
        } else if (inquiry.remote_errors && Object.keys(inquiry.remote_errors).length > 0) {
          // Map remote errors to form fields
          for (let key in inquiry.remote_errors) {
            const errorMessage = inquiry.remote_errors[key];

            // Always show the error in NOFIELD
            this.$set(this.errors, 'NOFIELD', errorMessage);

            // Also set specific field errors if applicable
            if (key === 'APIResponseMessage' || key === 'ErrorDescription') {
              if (
                errorMessage.toLowerCase().includes('dob') ||
                errorMessage.toLowerCase().includes('date of birth') ||
                errorMessage.toLowerCase().includes('birth date')
              ) {
                this.$set(this.errors, 'patient_dob', errorMessage);
              }
              if (errorMessage.toLowerCase().includes('name')) {
                if (errorMessage.toLowerCase().includes('first')) {
                  this.$set(this.errors, 'patient_first_name', errorMessage);
                } else if (errorMessage.toLowerCase().includes('last')) {
                  this.$set(this.errors, 'patient_last_name', errorMessage);
                }
              }
              if (
                errorMessage.toLowerCase().includes('member') ||
                errorMessage.toLowerCase().includes('id')
              ) {
                this.$set(this.errors, 'member_id', errorMessage);
              }
            }
          }
        } else if (inquiry.status === 'max-retry') {
          this.$buefy.toast.open({
            message: 'The payer is taking a long time to respond. Please try again later.',
            type: 'is-warning',
            duration: 20000,
          });
          this.$router.push({ name: 'InquiryList' });
        } else {
          this.$buefy.toast.open({
            message: `Failed to create inquiry: Unexpected status ${inquiry.status}`,
            type: 'is-danger',
          });
        }
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to create inquiry',
            type: 'is-danger',
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    async handleSaveAsDraft() {
      this.isSubmitting = true;
      this.errors = {};

      try {
        await request('/inquiries/draft', {
          method: 'POST',
          data: {
            provider_id: this.form.provider_id,
            payer_id: this.form.payer_id,
            memberId: this.form.member_id,
            patientBirthDate: this.form.patient_dob ? this.form.patient_dob.toISOString() : null,
            patientFirstName: this.form.patient_first_name,
            patientLastName: this.form.patient_last_name,
            subscriberRelationship: this.form.subscriber_relationship,
            serviceType: this.form.service_types,
            insurance_discovery_id: this.$route.params.id,
          },
        });

        this.$buefy.toast.open({
          message: 'Inquiry saved as draft successfully',
          type: 'is-success',
        });

        this.$router.push({ name: 'InquiryList' });
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to save inquiry as draft',
            type: 'is-danger',
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    handleAsOfTodayChange() {
      // Implementation of handleAsOfTodayChange method
    },
    cleanForm() {
      this.schema = null;
      this.combos = null;
      this.form.selectedCombo = 0;
      this.form.service_types = [];
      this.errors = {};
    },
    updateSchema(payer_id) {
      this.loadingPayers = true;
      request(`/payers/${payer_id}/schema`)
        .then((resp) => {
          this.schema = resp.data.schema.fields;
          this.combos = resp.data.schema.combos;
        })
        .catch(() => {
          this.schema = null;
          this.combos = null;
          this.errors.payer_id = 'The payer is having issues.';
        })
        .finally(() => {
          this.loadingPayers = false;
        });
    },
    handlePayerSelected(payer_id) {
      this.cleanForm();
      this.form.payer_id = payer_id;
      if (payer_id == null) {
        return;
      }
      this.updateSchema(payer_id);
    },
  },
};
</script>

<style scoped>
.box {
  padding: 2rem;
}

.block {
  margin-bottom: 1.5rem;
}

.buttons {
  margin-top: 1.5rem;
}

.radio-group {
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  margin-top: 0.5rem;
}

.label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}
</style>
